import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Page } from '@shopify/polaris';

interface AppLayoutProps {
  children: React.ReactNode;
}

// Define navigation structure
const navigationItems = [
  { id: 'dashboard', label: 'Dashboard', path: '/' },
  { id: 'inventory-analytics', label: 'Inventory Analytics', path: '/inventory-analytics' },
  { id: 'purchase-orders', label: 'Purchase Orders', path: '/purchase-orders' },
  { id: 'outsourced-orders', label: 'Outsourced Orders', path: '/outsourced-orders' },
  { id: 'manufacturing-orders', label: 'Manufacturing Orders', path: '/manufacturing-orders' },
  { id: 'settings', label: 'Settings', path: '/settings' },
];

// Get page title based on current path
const getPageTitle = (pathname: string): string => {
  switch (pathname) {
    case '/':
      return 'MRP Simple';
    case '/inventory-analytics':
      return 'Inventory Analytics';
    case '/purchase-orders':
      return 'Purchase Orders';
    case '/outsourced-orders':
      return 'Outsourced Orders';
    case '/manufacturing-orders':
      return 'Manufacturing Orders';
    case '/settings':
      return 'Settings';
    default:
      if (pathname.startsWith('/purchase-orders/')) {
        return 'Purchase Order Details';
      }
      return 'MRP Simple';
  }
};

// Get breadcrumbs for detail pages
const getBreadcrumbs = (pathname: string) => {
  if (pathname.startsWith('/purchase-orders/') && pathname !== '/purchase-orders') {
    return [{ content: 'Purchase Orders', url: '/purchase-orders' }];
  }
  return [];
};

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize App Bridge navigation using web components
  useEffect(() => {
    const initializeNavigation = () => {
      // Check if we're in an embedded Shopify context
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      const isEmbedded = !!shop && shop !== 'dev-shop.myshopify.com';

      if (!isEmbedded) {
        console.log('🔧 Development mode - App Bridge navigation disabled');
        return;
      }

      try {
        // Set up or update navigation menu using modern approach
        let navMenu = document.querySelector('ui-nav-menu');
        if (!navMenu) {
          navMenu = document.createElement('ui-nav-menu');
          document.body.appendChild(navMenu);
        }

        // Build navigation HTML
        const navHTML = navigationItems
          .map(item => {
            const isActive = item.path === '/' 
              ? location.pathname === '/' 
              : location.pathname.startsWith(item.path);
            
            return `<a href="${item.path}"${isActive ? ' aria-current="page"' : ''}>${item.label}</a>`;
          })
          .join('');

        navMenu.innerHTML = navHTML;

        // Handle navigation clicks
        const handleNavClick = (event: Event) => {
          const target = event.target as HTMLAnchorElement;
          if (target.tagName === 'A' && target.href) {
            event.preventDefault();
            const url = new URL(target.href);
            navigate(url.pathname);
          }
        };

        // Remove existing listener and add new one
        navMenu.removeEventListener('click', handleNavClick);
        navMenu.addEventListener('click', handleNavClick);

        console.log('✅ App Bridge navigation updated');
      } catch (error) {
        console.warn('⚠️ Failed to setup App Bridge navigation:', error);
      }
    };

    // Initialize navigation immediately and on path changes
    initializeNavigation();
  }, [navigate, location.pathname]);

  // Initialize App Bridge title bar
  useEffect(() => {
    const initializeTitleBar = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      const isEmbedded = !!shop && shop !== 'dev-shop.myshopify.com';

      if (!isEmbedded) {
        return;
      }

      try {
        let titleBar = document.querySelector('ui-title-bar');
        if (!titleBar) {
          titleBar = document.createElement('ui-title-bar');
          document.body.appendChild(titleBar);
        }

        const pageTitle = getPageTitle(location.pathname);
        const breadcrumbs = getBreadcrumbs(location.pathname);

        // Build title bar content
        titleBar.setAttribute('title', pageTitle);
        
        if (breadcrumbs.length > 0) {
          // Clear existing content and set breadcrumbs
          titleBar.innerHTML = '';
          breadcrumbs.forEach(crumb => {
            const link = document.createElement('a');
            link.href = crumb.url;
            link.textContent = crumb.content;
            titleBar.appendChild(link);
          });
        } else {
          // Clear any existing breadcrumbs
          titleBar.innerHTML = '';
        }

        // Handle breadcrumb clicks
        const handleBreadcrumbClick = (event: Event) => {
          const target = event.target as HTMLAnchorElement;
          if (target.tagName === 'A' && target.href) {
            event.preventDefault();
            const url = new URL(target.href);
            navigate(url.pathname);
          }
        };

        titleBar.removeEventListener('click', handleBreadcrumbClick);
        titleBar.addEventListener('click', handleBreadcrumbClick);

        console.log('✅ App Bridge title bar updated');
      } catch (error) {
        console.warn('⚠️ Failed to setup App Bridge title bar:', error);
      }
    };

    initializeTitleBar();
  }, [navigate, location.pathname]);

  // Check if we're in embedded mode to determine layout
  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get('shop');
  const isEmbedded = !!shop && shop !== 'dev-shop.myshopify.com';

  // Base layout styles with proper constraints
  const baseLayoutStyles = {
    width: '100%',
    maxWidth: '100vw',
    minHeight: '100vh',
    padding: 'var(--p-space-400)',
    backgroundColor: 'var(--p-color-bg-surface-secondary)',
    overflowX: 'hidden' as const,
    overflowY: 'auto' as const,
    boxSizing: 'border-box' as const,
  };

  // For embedded apps, use minimal layout since App Bridge handles navigation
  if (isEmbedded) {
    return (
      <div style={baseLayoutStyles}>
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}>
          {children}
        </div>
      </div>
    );
  }

  // For development mode, use Polaris Page component
  const pageTitle = getPageTitle(location.pathname);

  return (
    <div style={baseLayoutStyles}>
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}>
        <Page title={pageTitle}>
          <div style={{
            width: '100%',
            boxSizing: 'border-box',
          }}>
            {children}
          </div>
        </Page>
      </div>
    </div>
  );
}

// Extend window interface for global access
declare global {
  interface Window {
    shopifyParams?: {
      shop: string;
      host: string;
    };
  }
}
